import { createSlice } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';

import { getValidatedSkusByPromo, getParts, getProductConfigurations } from '../promostandards';

const slice = createSlice({
  name: 'convertibleProducts',
  initialState: {
    products: [],
    loading: 'idle', // 'idle' | 'pending'
    error: null,
  },
  reducers: {
    loading(state) {
      if (state.loading === 'idle') {
        state.loading = 'pending';
        state.error = null;
      }
    },
    received(state, action) {
      if (state.loading === 'pending') {
        state.products = action.payload || [];
        state.loading = 'idle';
      }
    },
    failed(state, action) {
      if (state.loading === 'pending') {
        state.loading = 'idle';
        state.error = action.payload || 'Failed to fetch convertible products';
      }
    },
    reset(state) {
      state.products = [];
      state.loading = 'idle';
      state.error = null;
    },
    removeConvertible: (state, action) => {
      const { promo_api_name, item_sku } = action.payload;

      state.products = state.products.filter(
        p => !(
          p.promo_api_name?.toLowerCase() === promo_api_name?.toLowerCase() &&
          p.ps_product_id === item_sku
        )
      );
    },
  },
});

export const { loading, received, failed, reset, removeConvertible } = slice.actions;

/**
 * Fetch convertibleProducts for the current order.
 * nonPSItems shape:
 * [{ promo_api_name, sku, currency_id, hasItemLocations }]
 */
export const getConvertibleProducts = (nonPSItems) => async (dispatch) => {
  dispatch(loading());

  try {
    const convertibleSkus = await getValidatedSkusByPromo(nonPSItems);
    if (!Array.isArray(convertibleSkus) || isEmpty(convertibleSkus)) {
      dispatch(received([]));
      return [];
    }

    const [psParts, psProductConfigurations] = await Promise.all([
      Promise.all(
        convertibleSkus.map((product) =>
          getParts({
            product: product.id,
            currency: product.currency_id,
            fields: JSON.stringify({ sku: null, colors: null, label_size: null, id: null }),
          }).then((parts) => ({
            parts: parts?.results,
            ps_product_id: product.ps_product_id,
            promo_api_name: product.promo_api_name,
            ext_product_id: product.id,
          }))
        )
      ),
      Promise.all(
        convertibleSkus
          .map((product) =>
            getProductConfigurations({ productId: product.id }).then((configurations) => ({
              ...configurations,
              promo_api_name: product.promo_api_name,
            }))
          )
      ),
    ]);

    const createKey = (promo_api_name, ps_product_id) =>
      `${promo_api_name?.toLowerCase()}::${ps_product_id}`;

    const configurationsMap = new Map(
      (psProductConfigurations || []).map((configuration) => [
        createKey(configuration.promo_api_name, configuration.ps_product_id),
        configuration,
      ])
    );

    const mergedProducts = (psParts || [])
      .filter((p) => Array.isArray(p.parts) && p.parts.length > 0)
      .map(p => {
        const productKey = createKey(p.promo_api_name, p.ps_product_id);
        return {
          ...p,
          ...configurationsMap.get(productKey),
        };
      });

    dispatch(received(mergedProducts));
    return mergedProducts;
  } catch (e) {
    dispatch(failed(e?.message));
    return [];
  }
};

export const resetConvertibleProducts = () => (dispatch) => dispatch(reset());

export default slice.reducer;